import { getSession } from '../session.js';
import { showToast } from './Toast.js';
import type { Customer } from '../../core/app-store.js';

type NavigateFn = (route: string, params?: Record<string, string>) => void;

export async function renderCustomerCRM(container: HTMLElement, navigate: NavigateFn): Promise<void> {
  const session = getSession();
  const businessId = session?.businessId ?? 'preview';

  container.innerHTML = `
    <div class="page-header">
      <div style="display:flex;justify-content:space-between;align-items:flex-start">
        <div>
          <h1 class="page-title">👥 Customer Database</h1>
          <p class="page-subtitle">Track customer visits, purchases, and lifetime value.</p>
        </div>
        <button class="btn btn-gold" id="add-customer-btn">+ Add Customer</button>
      </div>
    </div>

    <!-- Stats row -->
    <div class="stats-grid" id="crm-stats" style="margin-bottom:24px">
      <div class="stat-card"><div class="stat-value" id="stat-total">—</div><div class="stat-label">Total Customers</div></div>
      <div class="stat-card"><div class="stat-value" id="stat-revenue">—</div><div class="stat-label">Total Revenue</div></div>
      <div class="stat-card"><div class="stat-value" id="stat-avg">—</div><div class="stat-label">Avg Spend / Customer</div></div>
      <div class="stat-card"><div class="stat-value" id="stat-recent">—</div><div class="stat-label">New This Month</div></div>
    </div>

    <!-- Search -->
    <div style="margin-bottom:16px">
      <input type="text" id="crm-search" placeholder="Search by name, phone, or email…" style="max-width:400px" />
    </div>

    <!-- Add / Edit Modal -->
    <div id="customer-modal" style="display:none;position:fixed;inset:0;background:rgba(0,0,0,.6);z-index:1000;align-items:center;justify-content:center">
      <div class="card" style="width:460px">
        <h3 style="font-size:15px;font-weight:700;margin-bottom:16px" id="customer-modal-title">Add Customer</h3>
        <div class="form-row-2">
          <div>
            <label>Name *</label>
            <input type="text" id="c-name" placeholder="Full name" />
          </div>
          <div>
            <label>Phone</label>
            <input type="tel" id="c-phone" placeholder="(555) 000-0000" />
          </div>
        </div>
        <div class="form-row">
          <label>Email</label>
          <input type="email" id="c-email" placeholder="customer@email.com" />
        </div>
        <div class="form-row">
          <label>Notes</label>
          <textarea id="c-notes" placeholder="Allergies, preferences, VIP…" style="min-height:60px"></textarea>
        </div>
        <div style="display:flex;gap:10px;justify-content:flex-end;margin-top:8px">
          <button class="btn btn-ghost" id="close-customer-modal">Cancel</button>
          <button class="btn btn-gold" id="save-customer-btn">Save</button>
        </div>
      </div>
    </div>

    <!-- Customer table -->
    <div class="card" style="padding:0;overflow:hidden">
      <table id="crm-table" style="width:100%;border-collapse:collapse">
        <thead>
          <tr style="background:var(--bg-tertiary);font-size:11px;text-transform:uppercase;letter-spacing:.5px;color:var(--text-muted)">
            <th style="padding:10px 16px;text-align:left">Customer</th>
            <th style="padding:10px 16px;text-align:left">Contact</th>
            <th style="padding:10px 16px;text-align:right">Visits</th>
            <th style="padding:10px 16px;text-align:right">Total Spend</th>
            <th style="padding:10px 16px;text-align:left">First Visit</th>
            <th style="padding:10px 16px;text-align:left">Last Visit</th>
            <th style="padding:10px 16px;text-align:left">Favorites</th>
            <th style="padding:10px 8px"></th>
          </tr>
        </thead>
        <tbody id="crm-tbody">
          <tr><td colspan="8" style="text-align:center;padding:40px"><div class="spinner" style="margin:0 auto"></div></td></tr>
        </tbody>
      </table>
    </div>
  `;

  let editingId: string | null = null;
  let allCustomers: Customer[] = [];

  const openModal = (c?: Customer) => {
    editingId = c?.id ?? null;
    (document.getElementById('customer-modal-title') as HTMLElement).textContent = c ? 'Edit Customer' : 'Add Customer';
    (document.getElementById('c-name') as HTMLInputElement).value = c?.name ?? '';
    (document.getElementById('c-phone') as HTMLInputElement).value = c?.phone ?? '';
    (document.getElementById('c-email') as HTMLInputElement).value = c?.email ?? '';
    (document.getElementById('c-notes') as HTMLTextAreaElement).value = c?.notes ?? '';
    document.getElementById('customer-modal')!.style.display = 'flex';
  };

  document.getElementById('add-customer-btn')!.addEventListener('click', () => openModal());
  document.getElementById('close-customer-modal')!.addEventListener('click', () => {
    document.getElementById('customer-modal')!.style.display = 'none';
    editingId = null;
  });

  document.getElementById('save-customer-btn')!.addEventListener('click', async () => {
    const name = (document.getElementById('c-name') as HTMLInputElement).value.trim();
    const phone = (document.getElementById('c-phone') as HTMLInputElement).value.trim() || undefined;
    const email = (document.getElementById('c-email') as HTMLInputElement).value.trim() || undefined;
    const notes = (document.getElementById('c-notes') as HTMLTextAreaElement).value.trim() || undefined;
    if (!name) { showToast('Name is required', 'error'); return; }

    try {
      const api = getAPI();
      if (editingId) {
        await api.updateCustomer(editingId, { name, phone, email, notes });
        showToast('Customer updated', 'success');
      } else {
        await api.upsertCustomer(businessId, name, phone, email);
        if (notes) {
          // fetch the newly created customer and add notes
          const customers = await api.getCustomers(businessId) as Customer[];
          const found = customers.find((c) => c.name === name);
          if (found) await api.updateCustomer(found.id, { notes });
        }
        showToast('Customer added', 'success');
      }
    } catch {
      showToast('Saved (preview mode)', 'info');
    }
    document.getElementById('customer-modal')!.style.display = 'none';
    editingId = null;
    await load();
  });

  document.getElementById('crm-search')!.addEventListener('input', (e) => {
    const q = (e.target as HTMLInputElement).value.toLowerCase();
    const filtered = q
      ? allCustomers.filter((c) =>
          c.name.toLowerCase().includes(q) ||
          (c.phone ?? '').includes(q) ||
          (c.email ?? '').toLowerCase().includes(q)
        )
      : allCustomers;
    renderTable(filtered, openModal, businessId);
  });

  const load = async () => {
    try {
      allCustomers = await getAPI().getCustomers(businessId) as Customer[];
    } catch {
      allCustomers = getSampleCustomers(businessId);
    }
    updateStats(allCustomers);
    renderTable(allCustomers, openModal, businessId);
  };

  await load();
}

function updateStats(customers: Customer[]): void {
  const totalRevenue = customers.reduce((s, c) => s + c.totalSpend, 0);
  const avgSpend = customers.length ? totalRevenue / customers.length : 0;
  const thisMonth = new Date();
  thisMonth.setDate(1);
  thisMonth.setHours(0, 0, 0, 0);
  const newThisMonth = customers.filter((c) => new Date(c.firstVisit) >= thisMonth).length;

  (document.getElementById('stat-total') as HTMLElement).textContent = String(customers.length);
  (document.getElementById('stat-revenue') as HTMLElement).textContent = `$${totalRevenue.toFixed(2)}`;
  (document.getElementById('stat-avg') as HTMLElement).textContent = `$${avgSpend.toFixed(2)}`;
  (document.getElementById('stat-recent') as HTMLElement).textContent = String(newThisMonth);
}

function renderTable(
  customers: Customer[],
  openModal: (c: Customer) => void,
  businessId: string
): void {
  const tbody = document.getElementById('crm-tbody')!;

  if (customers.length === 0) {
    tbody.innerHTML = `<tr><td colspan="8" style="text-align:center;padding:40px;color:var(--text-muted)">No customers found.</td></tr>`;
    return;
  }

  tbody.innerHTML = customers.map((c) => {
    const tenure = daysSince(c.firstVisit);
    const tenureLabel = tenure < 30 ? `${tenure}d` : tenure < 365 ? `${Math.floor(tenure / 30)}mo` : `${(tenure / 365).toFixed(1)}yr`;
    const favs = c.favoriteItems.slice(0, 3).join(', ') || '—';

    return `
      <tr style="border-top:1px solid var(--border);font-size:13px" data-id="${c.id}">
        <td style="padding:12px 16px">
          <div style="font-weight:600">${escHtml(c.name)}</div>
          <div style="font-size:10px;color:var(--text-muted)">Customer ${tenureLabel}</div>
        </td>
        <td style="padding:12px 16px;color:var(--text-secondary)">
          ${c.phone ? `<div>${escHtml(c.phone)}</div>` : ''}
          ${c.email ? `<div style="font-size:11px;color:var(--text-muted)">${escHtml(c.email)}</div>` : ''}
        </td>
        <td style="padding:12px 16px;text-align:right;font-weight:600">${c.visitCount}</td>
        <td style="padding:12px 16px;text-align:right;font-weight:700;color:var(--brand-gold)">$${c.totalSpend.toFixed(2)}</td>
        <td style="padding:12px 16px;color:var(--text-muted);font-size:12px">${fmtDate(c.firstVisit)}</td>
        <td style="padding:12px 16px;color:var(--text-muted);font-size:12px">${fmtDate(c.lastVisit)}</td>
        <td style="padding:12px 16px;color:var(--text-secondary);font-size:11px;max-width:160px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${escHtml(favs)}</td>
        <td style="padding:12px 8px">
          <button class="btn btn-ghost btn-sm" data-action="edit" data-id="${c.id}">Edit</button>
        </td>
      </tr>
      ${c.notes ? `
        <tr style="background:rgba(245,158,11,.05)">
          <td colspan="8" style="padding:4px 16px 8px;font-size:11px;color:var(--warning)">📝 ${escHtml(c.notes)}</td>
        </tr>
      ` : ''}
    `;
  }).join('');

  tbody.querySelectorAll('[data-action="edit"]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const id = btn.getAttribute('data-id')!;
      const customer = customers.find((c) => c.id === id);
      if (customer) openModal(customer);
    });
  });
}

function daysSince(isoDate: string): number {
  return Math.floor((Date.now() - new Date(isoDate).getTime()) / 86400000);
}

function fmtDate(isoDate: string): string {
  return new Date(isoDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: '2-digit' });
}

function getSampleCustomers(businessId: string): Customer[] {
  const now = new Date();
  const ago = (days: number) => new Date(now.getTime() - days * 86400000).toISOString();
  return [
    {
      id: 'c1', businessId, name: 'Sarah Mitchell', phone: '(555) 123-4567', email: 'sarah@email.com',
      firstVisit: ago(365), lastVisit: ago(2), visitCount: 48, totalSpend: 1240.50,
      favoriteItems: ['Grilled Salmon', 'Craft Lemonade', 'Lava Cake'],
    },
    {
      id: 'c2', businessId, name: 'James Rodriguez', phone: '(555) 234-5678',
      firstVisit: ago(180), lastVisit: ago(7), visitCount: 22, totalSpend: 680.00,
      favoriteItems: ['Classic Burger', 'BBQ Bacon Burger'],
    },
    {
      id: 'c3', businessId, name: 'Emily Chen', email: 'emily.chen@corp.com',
      firstVisit: ago(90), lastVisit: ago(14), visitCount: 12, totalSpend: 430.75,
      favoriteItems: ['Caesar Salad', 'Chicken Alfredo'],
      notes: 'Gluten-free, prefers booth seating',
    },
    {
      id: 'c4', businessId, name: 'Marcus Thompson', phone: '(555) 345-6789',
      firstVisit: ago(730), lastVisit: ago(1), visitCount: 120, totalSpend: 3800.00,
      favoriteItems: ['Grilled Salmon', 'Mozzarella Sticks', 'Craft Lemonade'],
      notes: 'VIP — Friday nights, Table 5',
    },
    {
      id: 'c5', businessId, name: 'Linda Park',
      firstVisit: ago(15), lastVisit: ago(15), visitCount: 1, totalSpend: 52.00,
      favoriteItems: ['Chicken Alfredo'],
    },
  ];
}

function getAPI() {
  return (window as unknown as { preuve: Record<string, (...args: unknown[]) => Promise<unknown>> }).preuve;
}
function escHtml(str: string): string {
  return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
